import { Footer } from "@/components/layout/Footer";
import { Navbar } from "@/components/layout/Navbar";
import { ProductGrid } from "@/components/products/ProductGrid";
import { ProductReviews } from "@/components/products/ProductReviews";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { getProductIngredients } from "@/hooks/use-ingredients";
import { useProductViews } from "@/hooks/use-product-views";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import {
	AlertTriangle,
	ArrowLeft,
	Check,
	Lock,
	MapPin,
	MinusCircle,
	Package,
	PlusCircle,
	ShoppingCart,
	Store,
	TestTube,
	Truck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

interface ProductImage {
	id: string;
	product_id: string;
	image_url: string;
	is_primary: boolean;
}

interface SupplierProfile {
	id: string;
	name: string | null;
	business_name: string | null;
	account_type: string;
	profile_image?: string;
	location?: string;
	description?: string;
	minimum_free_shipping: number | null;
	shipping_cost: number;
	minimum_order: number;
	min_delivery_days?: number;
	max_delivery_days?: number;
	shipping_cost_type: string;
}

interface Tag {
	id: string;
	name: string;
}

interface Ingredient {
	id: string;
	name: string;
}

interface Product {
	id: string;
	name: string;
	description: string;
	price: number;
	categoryName: string;
	brand: string;
	supplier_id: string;
	created_at: string;
	tags: Tag[];
	images: ProductImage[];
	msrp: number | null;
	case_quantity: number;
	category_id: string | null;
	sampleAvailable: boolean;
	sampleCost: number;
	sampleQuantity: number;
}

interface FormattedProduct {
	id: string;
	name: string;
	brand: string;
	description: string;
	price: number;
	image: string;
	category: string;
	categoryId: string | null;
	tags: string[];
	supplierId: string;
	isFeatured?: boolean;
	isNew?: boolean;
	msrp?: number;
	caseQuantity: number;
}

interface ProfitCalculation {
	retailPrice: number;
	profit: number;
	margin: number;
}

const ProductDetail = () => {
	const { id } = useParams<{ id: string }>();
	const [product, setProduct] = useState<Product | null>(null);
	const [supplierProfile, setSupplierProfile] =
		useState<SupplierProfile | null>(null);
	const [loading, setLoading] = useState(true);
	const [activeImage, setActiveImage] = useState<string | null>(null);
	const [similarProducts, setSimilarProducts] = useState<FormattedProduct[]>(
		[]
	);
	const [quantity, setQuantity] = useState(1);
	const { toast } = useToast();
	const { addToCart } = useCart();
	const [isAddingToCart, setIsAddingToCart] = useState(false);
	const [isAddedToCart, setIsAddedToCart] = useState(false);
	const [retailPrice, setRetailPrice] = useState<number>(0);
	const [isPerCase, setIsPerCase] = useState<boolean>(false);
	const { user } = useAuth();
	const [ingredients, setIngredients] = useState<Ingredient[]>([]);

	useProductViews(id);

	useEffect(() => {
		if (id) {
			fetchProduct(id);
		}
	}, [id]);

	const fetchProduct = async (productId: string) => {
		try {
			setLoading(true);

			// Fetch product details
			const { data: productData, error: productError } = await supabase
				.from("products")
				.select("*")
				.eq("id", productId)
				.single();

			if (productError) throw productError;

			setRetailPrice(productData.msrp);

			// Fetch product images
			const { data: imagesData, error: imagesError } = await supabase
				.from("product_images")
				.select("*")
				.eq("product_id", productId);

			if (imagesError) throw imagesError;

			// Fetch supplier profile
			const { data: supplierData, error: supplierError } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", productData.supplier_id)
				.single();

			if (supplierError) {
				console.error("Error fetching supplier:", supplierError);
			} else {
				setSupplierProfile(supplierData);
			}

			// Fetch product tags
			const { data: tagsData, error: tagsError } = await supabase
				.from("product_tags")
				.select("tags(id, name)")
				.eq("product_id", productId);

			if (tagsError) {
				console.error("Error fetching tags:", tagsError);
			}

			// Get category name from category ID
			let categoryName = "Uncategorized";
			if (productData.category_id) {
				const { data: categoryData } = await supabase
					.from("categories")
					.select("name")
					.eq("id", productData.category_id)
					.single();

				if (categoryData) {
					categoryName = categoryData.name;
				}
			}

			// Format tags data
			const formattedTags: Tag[] = tagsData
				? tagsData.map((item) => ({
						id: item.tags.id,
						name: item.tags.name,
				  }))
				: [];

			// Combine product with its images and tags
			const productWithImages: Product = {
				...productData,
				images: imagesData || [],
				tags: formattedTags,
				brand:
					supplierData?.business_name ||
					supplierData?.name ||
					"Unknown Brand",
				categoryName: categoryName,
				sampleAvailable: productData.sample_available,
				sampleCost: productData.sample_cost,
				sampleQuantity: productData.sample_quantity,
			};

			setProduct(productWithImages);
			setQuantity(1); // Set initial quantity to 1

			// Set the primary image as active image, or first available image
			const primaryImage = imagesData.find((img) => img.is_primary);
			setActiveImage(
				primaryImage?.image_url || imagesData[0]?.image_url || null
			);

			// Fetch similar products from the same category
			if (productData.category_id) {
				fetchSimilarProducts(productData.category_id, productId);
			}

			// Fetch ingredients
			console.log("Fetching ingredients for product:", productId);
			const ingredients = await getProductIngredients(productId);
			console.log("Ingredients fetched:", ingredients);
			setIngredients(ingredients);
		} catch (error) {
			console.error("Error fetching product:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchSimilarProducts = async (
		categoryId: string,
		currentProductId: string
	) => {
		try {
			// Fetch products from the same category excluding the current product
			const { data: productsData, error: productsError } = await supabase
				.from("products")
				.select("*")
				.eq("category_id", categoryId)
				.neq("id", currentProductId)
				.limit(4);

			if (productsError) throw productsError;

			if (productsData.length > 0) {
				// Fetch images for similar products
				const productIds = productsData.map((product) => product.id);

				const { data: imagesData, error: imagesError } = await supabase
					.from("product_images")
					.select("*")
					.in("product_id", productIds);

				if (imagesError) throw imagesError;

				// Fetch supplier profiles
				const supplierIds = productsData.map(
					(product) => product.supplier_id
				);
				const { data: suppliersData, error: suppliersError } =
					await supabase
						.from("profiles")
						.select("*")
						.in("id", supplierIds);

				if (suppliersError) throw suppliersError;

				// Create a map of supplier IDs to names
				const supplierMap = new Map();
				suppliersData.forEach((profile) => {
					supplierMap.set(
						profile.id,
						profile.business_name ||
							profile.name ||
							"Unknown Supplier"
					);
				});

				// Fetch category names
				const { data: categoriesData } = await supabase
					.from("categories")
					.select("id, name");

				const categoryMap = new Map();
				if (categoriesData) {
					categoriesData.forEach((cat) => {
						categoryMap.set(cat.id, cat.name);
					});
				}

				// Format similar products for ProductGrid
				const formattedProducts: FormattedProduct[] = productsData.map(
					(product) => {
						const productImages = imagesData.filter(
							(img) => img.product_id === product.id
						);
						const primaryImage = productImages.find(
							(img) => img.is_primary
						);
						const imageUrl =
							primaryImage?.image_url ||
							productImages[0]?.image_url ||
							"/placeholder.svg";

						return {
							id: product.id,
							name: product.name,
							brand:
								supplierMap.get(product.supplier_id) ||
								"Unknown Brand",
							description: product.description || "",
							price: product.price,
							image: imageUrl,
							category:
								categoryMap.get(product.category_id) ||
								"Uncategorized",
							categoryId: product.category_id,
							tags: [], // Initialize with empty array
							supplierId: product.supplier_id,
							msrp: product.msrp || undefined,
							caseQuantity: product.case_quantity || 1,
						};
					}
				);

				setSimilarProducts(formattedProducts);
			}
		} catch (error) {
			console.error("Error fetching similar products:", error);
		}
	};

	const handleAddToCart = () => {
		if (!product) return;

		setIsAddingToCart(true);

		// Add to cart
		addToCart({
			id: product.id,
			name: product.name,
			price: product.price,
			quantity: quantity,
			image: activeImage || "/placeholder.svg",
			supplierId: product.supplier_id,
			caseQuantity: product.case_quantity || 1,
		});

		// Set visual state
		setTimeout(() => {
			setIsAddingToCart(false);
			setIsAddedToCart(true);

			// Show toast
			toast({
				title: "Added to cart",
				description: `${product.name} has been added to your cart`,
			});

			// Reset "Added" state after 2 seconds
			setTimeout(() => {
				setIsAddedToCart(false);
			}, 2000);
		}, 600);
	};

	const incrementQuantity = () => {
		setQuantity((prev) => prev + 1);
	};

	const decrementQuantity = () => {
		setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
	};

	// Check if shipping will be free based on supplier's min free shipping threshold
	const isShippingFree = () => {
		if (!product || !supplierProfile) return false;

		// Free if minimum_free_shipping is 0 (no free shipping threshold)
		if (supplierProfile.minimum_free_shipping === null) return false;

		// Free if order total meets or exceeds minimum free shipping threshold
		const orderTotal = product.price * quantity;
		return orderTotal >= supplierProfile.minimum_free_shipping;
	};

	const calculateShippingCost = () => {
		if (!product || !supplierProfile) return 0;

		// If order meets free shipping threshold, shipping is free
		if (isShippingFree()) return 0;

		const orderTotal = product.price * quantity * product.case_quantity;

		// Calculate based on shipping cost type
		if (supplierProfile.shipping_cost_type === "percentage") {
			return (orderTotal * supplierProfile.shipping_cost) / 100;
		}

		// Fixed shipping cost
		return supplierProfile.shipping_cost;
	};

	const calculateProfit = (): ProfitCalculation | null => {
		if (!product || !retailPrice) return null;

		const costPrice = isPerCase
			? product.price * product.case_quantity
			: product.price;

		const profit = retailPrice - costPrice;
		const margin = (profit / retailPrice) * 100;

		return {
			retailPrice,
			profit,
			margin,
		};
	};

	// Display loading state
	if (loading) {
		return (
			<div className="min-h-screen flex flex-col">
				<Navbar />
				<main className="flex-grow pt-20">
					<div className="container max-w-6xl mx-auto px-6 md:px-10 py-10">
						<div className="flex flex-col md:flex-row gap-10">
							{/* Image skeleton */}
							<div className="md:w-1/2">
								<Skeleton className="aspect-square w-full rounded-lg" />
								<div className="flex gap-2 mt-4">
									{[1, 2, 3].map((i) => (
										<Skeleton
											key={i}
											className="w-20 h-20 rounded"
										/>
									))}
								</div>
							</div>

							{/* Content skeleton */}
							<div className="md:w-1/2">
								<Skeleton className="h-10 w-3/4 mb-4" />
								<Skeleton className="h-6 w-1/4 mb-6" />
								<Skeleton className="h-6 w-1/3 mb-4" />
								<Skeleton className="h-32 w-full mb-6" />
								<Skeleton className="h-12 w-full mb-4" />
								<Skeleton className="h-12 w-full" />
							</div>
						</div>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	// Display error if product not found
	if (!product) {
		return (
			<div className="min-h-screen flex flex-col">
				<Navbar />
				<main className="flex-grow pt-20">
					<div className="container max-w-6xl mx-auto px-6 md:px-10 py-20 text-center">
						<h1 className="text-3xl font-display font-semibold mb-4">
							Product Not Found
						</h1>
						<p className="text-gray-600 mb-8">
							The product you're looking for doesn't exist or has
							been removed.
						</p>
						<Link to="/products">
							<Button className="gap-2">
								<ArrowLeft className="h-4 w-4" />
								Back to Products
							</Button>
						</Link>
					</div>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col">
			<Navbar />
			<main className="flex-grow pt-20">
				<div className="container max-w-6xl mx-auto px-6 md:px-10 py-10">
					{/* Breadcrumb navigation */}
					<div className="flex text-sm text-gray-500 mb-8 items-center">
						<Link to="/" className="hover:text-primary">
							Home
						</Link>
						<span className="mx-2">/</span>
						<Link to="/products" className="hover:text-primary">
							Products
						</Link>
						<span className="mx-2">/</span>
						<span className="text-gray-700 font-medium truncate">
							{product?.name}
						</span>
					</div>

					{/* Product details section */}
					<div className="flex flex-col md:flex-row gap-10 mb-16">
						{/* Product images */}
						<div className="md:w-1/2">
							{/* Main image */}
							<div className="bg-gray-50 rounded-lg overflow-hidden mb-4 aspect-square">
								{activeImage ? (
									<img
										src={activeImage}
										alt={product?.name}
										className="w-full h-full object-contain"
									/>
								) : (
									<div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
										No image available
									</div>
								)}
							</div>

							{/* Thumbnail gallery */}
							{product?.images.length > 0 && (
								<div className="flex gap-3 flex-wrap">
									{product.images.map((image) => (
										<button
											key={image.id}
											className={`w-20 h-20 rounded-md overflow-hidden border-2 ${
												activeImage === image.image_url
													? "border-primary"
													: "border-transparent"
											}`}
											onClick={() =>
												setActiveImage(image.image_url)
											}>
											<img
												src={image.image_url}
												alt={`${product.name} thumbnail`}
												className="w-full h-full object-cover"
											/>
										</button>
									))}
								</div>
							)}

							{/* Profit Calculator */}
							{user && (
								<div className="border rounded-lg p-4 bg-gray-50 mt-4">
									<div className="flex items-center justify-between mb-3">
										<h3 className="font-medium text-gray-700">
											Profit Calculator
										</h3>
										<div className="flex items-center gap-2">
											<span className="text-sm text-gray-600">
												Calculate by:
											</span>
											<Button
												variant="outline"
												size="sm"
												onClick={() =>
													setIsPerCase(!isPerCase)
												}>
												{isPerCase ? "Case" : "Unit"}
											</Button>
										</div>
									</div>

									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<span className="text-sm text-gray-600 min-w-[100px]">
												Retail Price:
											</span>
											<input
												type="number"
												value={retailPrice || ""}
												onChange={(e) =>
													setRetailPrice(
														Number(e.target.value)
													)
												}
												className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
												placeholder={`Enter ${
													isPerCase ? "case" : "unit"
												} retail price`}
											/>
										</div>

										{calculateProfit() && (
											<>
												<div className="flex items-center gap-2">
													<span className="text-sm text-gray-600 min-w-[100px]">
														Cost Price:
													</span>
													<span className="text-sm font-medium">
														{formatCurrency(
															isPerCase
																? product.price *
																		product.case_quantity
																: product.price
														)}
														{isPerCase
															? " per case"
															: " per unit"}
													</span>
												</div>
												<div className="flex items-center gap-2">
													<span className="text-sm text-gray-600 min-w-[100px]">
														Profit:
													</span>
													<span className="text-sm font-medium text-green-600">
														{formatCurrency(
															calculateProfit()!
																.profit
														)}
														{isPerCase
															? " per case"
															: " per unit"}
													</span>
												</div>
												<div className="flex items-center gap-2">
													<span className="text-sm text-gray-600 min-w-[100px]">
														Margin:
													</span>
													<span className="text-sm font-medium">
														{calculateProfit()!.margin.toFixed(
															1
														)}
														%
													</span>
												</div>
											</>
										)}
									</div>
								</div>
							)}
						</div>

						{/* Product information */}
						<div className="md:w-1/2">
							{/* Tags */}
							<div className="flex flex-wrap gap-2 mb-3">
								{product?.categoryName && (
									<span className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded text-xs font-medium">
										{product.categoryName}
									</span>
								)}
								{calculateShippingCost() === 0 && (
									<span className="bg-green-100 text-green-700 px-2.5 py-1 rounded text-xs font-medium">
										Free Shipping
									</span>
								)}

								{/* Product tags */}
								{product?.tags &&
									product.tags.map((tag) => (
										<span
											key={tag.id}
											className="bg-blue-50 text-blue-600 px-2.5 py-1 rounded text-xs font-medium">
											{tag.name}
										</span>
									))}
							</div>

							{/* Product title and brand */}
							<h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-gray-900 mb-2">
								{product?.name}
							</h1>
							<div className="text-gray-500 mb-4 font-medium">
								<Link
									to={`/suppliers/${product?.supplier_id}`}
									className="flex items-center gap-1 hover:text-primary transition-colors">
									<Store className="h-4 w-4" />
									{product?.brand}
								</Link>
							</div>

							{/* Price, MSRP, and Profit Calculator */}
							<div className="mb-6 space-y-4">
								<div className="flex flex-col">
									{user ? (
										<span className="text-2xl md:text-3xl font-display text-gray-900">
											<strong>
												{formatCurrency(
													product?.price || 0
												)}
											</strong>{" "}
											<span className="text-sm">
												per item{" "}
											</span>
										</span>
									) : (
										<div className="flex items-center gap-2 text-gray-500">
											<Lock className="h-5 w-5" />
											<span className="text-xl font-medium">
												Login to view wholesale price
											</span>
										</div>
									)}
									{product?.msrp &&
										product.msrp > product.price && (
											<span className="text-sm text-gray-400 mt-1">
												MSRP:{" "}
												{formatCurrency(product.msrp)}
											</span>
										)}
								</div>

								<p className="text-sm text-gray-500 flex items-center">
									<Package className="h-3 w-3 mr-1 inline-block" />
									Sold in cases of {product.case_quantity}{" "}
									units
								</p>
							</div>

							{/* Description */}
							<div className="mb-6">
								<h3 className="text-gray-700 font-medium mb-2">
									Description
								</h3>
								<p className="text-gray-600">
									{product?.description}
								</p>
							</div>

							{/* Ingredients Section */}
							{ingredients.length > 0 && (
								<div className="mb-6">
									<h3 className="text-gray-700 font-medium mb-2">
										Ingredients
									</h3>
									<div className="flex flex-wrap gap-2">
										{ingredients.map((ingredient) => (
											<span
												key={ingredient.id}
												className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md text-sm">
												{ingredient.name}
											</span>
										))}
									</div>
								</div>
							)}

							{/* Quantity selector and Add to Cart */}
							<div className="mb-6">
								<h3 className="text-gray-700 font-medium mb-2">
									Quantity (Cases)
								</h3>
								<div className="flex gap-4">
									<div className="flex items-center gap-3">
										<Button
											variant="outline"
											size="icon"
											onClick={decrementQuantity}
											disabled={quantity <= 1}>
											<MinusCircle className="h-4 w-4" />
										</Button>
										<span className="w-12 text-center font-medium">
											{quantity}
										</span>
										<Button
											variant="outline"
											size="icon"
											onClick={incrementQuantity}>
											<PlusCircle className="h-4 w-4" />
										</Button>
									</div>

									<Button
										className="flex-1 gap-2"
										size="lg"
										onClick={handleAddToCart}
										disabled={
											isAddingToCart ||
											isAddedToCart ||
											!user
										}>
										{!user ? (
											<span className="flex items-center gap-2">
												<Lock className="h-5 w-5" />
												Login to Purchase
											</span>
										) : isAddingToCart ? (
											<span className="flex items-center gap-2">
												<span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
												Adding
											</span>
										) : isAddedToCart ? (
											<span className="flex items-center gap-2">
												<Check className="h-4 w-4" />
												Added
											</span>
										) : (
											<span className="flex items-center gap-2">
												<ShoppingCart className="h-5 w-5" />
												Add to Cart
											</span>
										)}
									</Button>
								</div>
								{product.case_quantity > 1 && (
									<p className="text-sm text-gray-500 mt-2">
										Total units:{" "}
										{quantity * product.case_quantity}
									</p>
								)}
							</div>

							{/* Shipping information */}
							<div className="mb-6 p-3 bg-gray-50 rounded-lg">
								<div className="flex items-center gap-2">
									<Truck className="h-4 w-4 text-gray-500" />
									<h3 className="font-medium text-gray-700">
										Shipping for {quantity}{" "}
										{quantity > 1 ? "cases" : "case"}
									</h3>
								</div>

								<div className="pl-6 mt-1.5 space-y-1">
									{calculateShippingCost() === 0 ? (
										<div className="text-green-600 font-medium">
											Free Shipping
										</div>
									) : (
										<div className="text-gray-600">
											{formatCurrency(
												calculateShippingCost()
											)}
										</div>
									)}

									{supplierProfile?.minimum_free_shipping >
										0 && (
										<div className="text-sm text-gray-500">
											Free shipping on orders over{" "}
											{formatCurrency(
												supplierProfile.minimum_free_shipping
											)}{" "}
											from this supplier
										</div>
									)}
								</div>
							</div>

							{/* Sample Information */}
							{product.sampleAvailable && (
								<div className="mt-6 p-4 bg-blue-50 rounded-lg">
									<div className="flex items-center gap-2 mb-2">
										<TestTube className="h-5 w-5 text-blue-600" />
										<h3 className="font-medium">
											Sample Available
										</h3>
									</div>
									<p className="text-sm text-gray-600 mb-3">
										Try before you buy with product samples.
										Limited to one sample per customer.
									</p>
									<div className="flex flex-wrap gap-4 items-center">
										<div>
											<span className="block text-sm text-gray-500">
												Sample Price:
											</span>
											<span className="font-medium">
												{formatCurrency(
													product.sampleCost
												)}
											</span>
										</div>
										<div>
											<span className="block text-sm text-gray-500">
												Quantity:
											</span>
											<span className="font-medium">
												{product.sampleQuantity} units
											</span>
										</div>
										<Button
											variant="outline"
											size="sm"
											className="ml-auto"
											onClick={() => {
												addToCart({
													id: product.id,
													name: `${product.name} (Sample)`,
													price:
														product.sampleCost /
														product.sampleQuantity,
													caseQuantity:
														product.sampleQuantity,
													quantity: 1,
													image: product.images[0]
														.image_url,
													supplierId:
														product.supplier_id,
													isSample: true,
												});
												toast({
													title: "Sample added to cart",
													description:
														"Sample has been added to your cart",
												});
											}}>
											<TestTube className="mr-2 h-4 w-4" />
											Add Sample to Cart
										</Button>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Reviews section */}
					<div className="mb-16">
						<h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900 mb-8">
							Customer Reviews
						</h2>
						<ProductReviews productId={id || ""} />
					</div>

					{/* About the Seller section */}
					{supplierProfile && (
						<div>
							<h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900 mb-8">
								About the Seller
							</h2>
							<div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 md:p-8">
								<div className="flex flex-col md:flex-row gap-8 items-center">
									<div className="md:w-1/4 flex flex-col items-center">
										<Avatar className="h-32 w-32 mb-4">
											<AvatarImage
												src={
													supplierProfile.profile_image ||
													undefined
												}
												alt={
													supplierProfile.business_name ||
													supplierProfile.name ||
													""
												}
											/>
											<AvatarFallback className="text-3xl bg-primary/10 text-primary">
												{(
													supplierProfile.business_name ||
													supplierProfile.name ||
													""
												)
													.split(" ")
													.map((n) => n[0])
													.join("")
													.toUpperCase()
													.slice(0, 2)}
											</AvatarFallback>
										</Avatar>

										{supplierProfile.location && (
											<div className="flex items-center gap-1 text-gray-500 text-sm">
												<MapPin className="h-4 w-4" />
												<span>
													{supplierProfile.location}
												</span>
											</div>
										)}
									</div>

									<div className="md:w-3/4">
										<div className="flex items-center gap-2 mb-1">
											<h3 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900">
												{supplierProfile.business_name ||
													supplierProfile.name}
											</h3>
										</div>

										<div className="bg-gray-50 p-4 rounded-lg mb-4">
											<div className="flex-1">
												<h4 className="font-medium text-gray-700 mb-2">
													About
												</h4>
												<p className="text-gray-600">
													{supplierProfile.description ||
														"No description available."}
												</p>
											</div>
										</div>

										<div className="space-y-3">
											{supplierProfile.minimum_free_shipping >
												0 && (
												<div className="bg-green-50 p-3 rounded-lg flex items-center gap-2">
													<Truck className="h-5 w-5 text-green-600" />
													<span className="text-green-700">
														Free shipping on orders
														over{" "}
														{formatCurrency(
															supplierProfile.minimum_free_shipping
														)}
													</span>
												</div>
											)}

											{supplierProfile.minimum_order >
												0 && (
												<div className="bg-amber-50 p-3 rounded-lg flex items-center gap-2">
													<AlertTriangle className="h-5 w-5 text-amber-600" />
													<span className="text-amber-700">
														Minimum order value:{" "}
														{formatCurrency(
															supplierProfile.minimum_order
														)}
													</span>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Similar products section */}
					{similarProducts.length > 0 && (
						<div className="mb-16">
							<h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900 mb-8">
								Similar Products
							</h2>
							<ProductGrid products={similarProducts} />
						</div>
					)}
				</div>
			</main>
			<Footer />
		</div>
	);
};

export default ProductDetail;
